import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, MapPin, Ruler, Sparkles, MessageCircle } from "lucide-react";
import { formatMXN, buildWhatsAppUrl, resolveImage } from "../lib/api";

function calcMonthly(price, down, months, annualRate) {
  const principal = Math.max(0, price - down);
  if (principal === 0 || months === 0) return 0;
  const r = annualRate / 100 / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

export default function PropertyModal({ property, open, onOpenChange, interestRate, onRequestContact }) {
  const [idx, setIdx] = useState(0);
  const [down, setDown] = useState(0);
  const [months, setMonths] = useState(24);

  const gallery = useMemo(
    () => (property?.gallery?.length ? property.gallery : property ? [property.image] : []),
    [property]
  );

  useEffect(() => {
    if (property) {
      setIdx(0);
      setDown(Math.round(property.price * 0.3));
      setMonths(24);
    }
  }, [property]);

  if (!property) return null;

  const monthly = calcMonthly(property.price, down, months, interestRate);
  const total = monthly * months + down;

  const waText = `Hola Pextlán, me interesa el lote #${property.lot_number} "${property.name}" (${property.surface.toLocaleString()} m² · ${formatMXN(property.price)}${property.is_presale ? " · PREVENTA" : ""}). ¿Podemos agendar una visita?`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="property-modal"
        className="max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto p-0 bg-[#0B1A0F] border-[#B8E07A]/25 text-[#F2EFE6] rounded-3xl"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{property.name}</DialogTitle>
          <DialogDescription>
            Detalle del terreno {property.name} en {property.location} con galería, ubicación y simulador de pago.
          </DialogDescription>
        </DialogHeader>

        {/* Carousel */}
        <div className="relative h-72 sm:h-96 overflow-hidden rounded-t-3xl">
          <AnimatePresence mode="wait">
            <motion.img
              key={gallery[idx]}
              src={resolveImage(gallery[idx])}
              alt={property.name}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A140F]/70 via-transparent to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 text-white flex items-end justify-between">
            <div>
              <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-[#F1C36B]">
                <MapPin className="w-3 h-3" /> {property.location}
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl mt-1">{property.name}</h2>
              <div className="font-fairy text-2xl text-[#B8E07A] -mt-1">
                Lote #{property.lot_number}{property.is_presale ? " · Preventa" : ""}
              </div>
            </div>
          </div>
          {gallery.length > 1 && (
            <>
              <button
                onClick={() => setIdx((i) => (i - 1 + gallery.length) % gallery.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-[#1C3B2B]"
                data-testid="carousel-prev"
                aria-label="Anterior"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIdx((i) => (i + 1) % gallery.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-[#1C3B2B]"
                data-testid="carousel-next"
                aria-label="Siguiente"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 right-6 flex gap-1.5">
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Imagen ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#C5A059]" : "w-1.5 bg-white/60"}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="px-6 sm:px-10 pb-10 pt-6 grid lg:grid-cols-5 gap-8">
          {/* Left: description + features */}
          <div className="lg:col-span-3">
            <div className="flex flex-wrap items-center gap-4 text-[#F2EFE6]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#B8E07A]">Precio total</div>
                <div className="font-serif text-3xl text-[#F1C36B]">{formatMXN(property.price)}</div>
                {property.price_per_m2 ? (
                  <div className="text-xs text-[#F2EFE6]/60 mt-0.5">${property.price_per_m2.toLocaleString()} / m²</div>
                ) : null}
              </div>
              <div className="h-10 w-px bg-[#B8E07A]/20" />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#B8E07A]">Superficie</div>
                <div className="inline-flex items-center gap-1 font-medium"><Ruler className="w-4 h-4 text-[#F1C36B]" /> {property.surface.toLocaleString()} m²</div>
              </div>
            </div>

            <p className="mt-6 text-[#F2EFE6]/80 leading-relaxed">{property.description}</p>

            {property.features?.length > 0 && (
              <div className="mt-6 grid grid-cols-2 gap-3">
                {property.features.map((f) => (
                  <div key={f} className="flex items-start gap-2 text-sm text-[#F2EFE6]/85">
                    <Sparkles className="w-4 h-4 text-[#F1C36B] mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Map */}
            {property.map_embed_url && (
              <div className="mt-8 rounded-2xl overflow-hidden border border-[#B8E07A]/20">
                <iframe
                  title="Ubicación"
                  data-testid="property-map"
                  src={property.map_embed_url}
                  className="w-full h-64"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
          </div>

          {/* Right: simulator */}
          <div id="simulador" className="lg:col-span-2">
            <div className="rounded-2xl border border-[#B8E07A]/20 bg-[#15301E] p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)] sticky top-6">
              <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#F1C36B]">
                Simulador de pago
              </div>
              <h3 className="mt-1 font-serif text-2xl text-[#F2EFE6]">
                Calcula tu mensualidad
              </h3>

              <div className="mt-6">
                <div className="flex items-center justify-between text-sm">
                  <label className="text-[#F2EFE6]/80">Enganche</label>
                  <span className="font-medium text-[#F1C36B]">{formatMXN(down)}</span>
                </div>
                <Slider
                  data-testid="simulator-downpayment"
                  value={[down]}
                  onValueChange={(v) => setDown(v[0])}
                  min={Math.round(property.price * 0.1)}
                  max={Math.round(property.price * 0.9)}
                  step={5000}
                  className="mt-3"
                />
                <div className="mt-1 text-[11px] text-[#F2EFE6]/55">
                  {Math.round((down / property.price) * 100)}% del precio total
                </div>
              </div>

              <div className="mt-6">
                <label className="text-sm text-[#F2EFE6]/80">Plazo (meses)</label>
                <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                  <SelectTrigger data-testid="simulator-months" className="mt-2 bg-[#0B1A0F] border-[#B8E07A]/20 text-[#F2EFE6]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B1A0F] text-[#F2EFE6] border-[#B8E07A]/20">
                    {[12, 18, 24, 36, 48, 60, 72].map((m) => (
                      <SelectItem key={m} value={String(m)}>{m} meses</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-7 p-5 rounded-xl bg-gradient-to-br from-[#0B1A0F] to-[#050D08] text-[#F2EFE6] border border-[#F1C36B]/30">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#F1C36B]">
                  Mensualidad estimada
                </div>
                <div data-testid="simulator-monthly" className="mt-1 font-serif text-4xl text-[#F1C36B]">
                  {formatMXN(monthly)}
                </div>
                <div className="mt-3 text-xs text-[#F2EFE6]/65 space-y-0.5">
                  <div>Tasa anual: <span className="text-[#B8E07A]">{interestRate}%</span></div>
                  <div>Total estimado: <span className="text-[#B8E07A]">{formatMXN(total)}</span></div>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-2">
                <a
                  href={buildWhatsAppUrl(waText)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="modal-whatsapp-btn"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white px-5 py-3 text-sm font-semibold btn-lift"
                >
                  <MessageCircle className="w-4 h-4" /> Lo quiero por WhatsApp
                </a>
                <Button
                  onClick={() => {
                    onOpenChange(false);
                    setTimeout(() => onRequestContact?.(property), 150);
                  }}
                  data-testid="modal-lead-btn"
                  variant="outline"
                  className="rounded-full border-[#F1C36B] text-[#F1C36B] hover:bg-[#F1C36B] hover:text-[#0B1A0F] bg-transparent"
                >
                  Déjame mis datos
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
